import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import TopTabbar from "../components/partials/TopTabbar";
import Header from "../components/partials/Header";
import Footer from "../components/partials/Footer";
import Button from "react-bootstrap/Button";
import TransferList from "../components/TransferList";
import RejectedTransferList from "../components/RejectedTransferList";
import ConfirmedTransferList from "../components/ConfirmedTransferList";
import TransferDetailModal from "../components/TransferDetailModal";
import PasteReferenceModal from "../components/PasteReferenceModal";
import ExportTransferModal from "../components/ExportTransferModal";
import TransferService from "../services/TransferService";
import UserService from "../services/UserService";
import AuthService from "../services/AuthService";
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import LoadingSpinner from '../utils/LoadingSpinner';

const PAGE_SIZE = 25;

// Mapping onglet → statut backend
const TAB_STATUS = {
    pending:   'PENDING',
    confirmed: 'SUCCESS',
    rejected:  'REJECTED',
};

// ── Utilitaire formatage date ─────────────────────────────────────────────────
const formatDateParam = (date) => {
    if (!date) return null;
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
};

const formatDateDisplay = (date) => {
    if (!date) return '';
    return date.toLocaleDateString('fr-FR');
};

const TransferPage = () => {
    // ─── Données ─────────────────────────────────────────────────────────────
    const [transfers,          setTransfers]          = useState([]);
    const [rejectedTransfers,  setRejectedTransfers]  = useState([]);
    const [confirmedTransfers, setConfirmedTransfers] = useState([]);

    // ─── Pagination ───────────────────────────────────────────────────────────
    const [currentPage,    setCurrentPage]    = useState(0);
    const [totalPages,     setTotalPages]     = useState(0);
    const [totalElements,  setTotalElements]  = useState(0);

    // ─── UI ───────────────────────────────────────────────────────────────────
    const [selectedTransfer,  setSelectedTransfer]  = useState(null);
    const [showDetailModal,   setShowDetailModal]   = useState(false);
    const [showPasteModal,    setShowPasteModal]    = useState(false);
    const [showExportModal,   setShowExportModal]   = useState(false);
    const [showFilterPanel,   setShowFilterPanel]   = useState(false);
    const [activeComponent,   setActiveComponent]   = useState('pending');
    const [isLoading,         setIsLoading]         = useState(false);
    const [isPageLoading,     setIsPageLoading]     = useState(true);
    const [currentUser,       setCurrentUser]       = useState(null);

    // ─── Filtre ───────────────────────────────────────────────────────────────
    const [filterMode,       setFilterMode]       = useState('date_range'); // 'date_range' | 'single_date' | 'by_user'
    const [filterStartDate,  setFilterStartDate]  = useState('');
    const [filterEndDate,    setFilterEndDate]    = useState('');
    const [filterSingleDate, setFilterSingleDate] = useState('');
    const [filterResults,    setFilterResults]    = useState(null); // null = pas de filtre actif
    const [filterLabel,      setFilterLabel]      = useState('');
    const [isFilterLoading,  setIsFilterLoading]  = useState(false);
    const [filterError,      setFilterError]      = useState('');

    // ─── Filtre utilisateur ───────────────────────────────────────────────────
    const [userSearch,       setUserSearch]       = useState('');
    const [allUsers,         setAllUsers]         = useState([]);
    const [userSuggestions,  setUserSuggestions]  = useState([]);
    const [selectedUser,     setSelectedUser]     = useState(null);
    const [loadingUsers,     setLoadingUsers]     = useState(false);
    const [userStartDate,    setUserStartDate]    = useState('');
    const [userEndDate,      setUserEndDate]      = useState('');

    const isAdmin = currentUser?.roleNames?.includes('ADMIN');

    // ─── Chargement utilisateurs ──────────────────────────────────────────────
    useEffect(() => {
        if (filterMode === 'by_user' && isAdmin && allUsers.length === 0) {
            setLoadingUsers(true);
            UserService.getAllUsers(0, 200)
                .then(data => setAllUsers(data?.content || []))
                .catch(e => console.error('Erreur chargement users:', e))
                .finally(() => setLoadingUsers(false));
        }
    }, [filterMode, isAdmin, allUsers.length]);

    // ─── Suggestions utilisateur ──────────────────────────────────────────────
    useEffect(() => {
        if (!userSearch.trim()) { setUserSuggestions([]); return; }
        const q = userSearch.toLowerCase();
        setUserSuggestions(
            allUsers
                .filter(u =>
                    u.username?.toLowerCase().includes(q) ||
                    u.firstName?.toLowerCase().includes(q) ||
                    u.lastName?.toLowerCase().includes(q)
                )
                .slice(0, 8)
        );
    }, [userSearch, allUsers]);

    // ─── Chargement d'une page ────────────────────────────────────────────────
    const fetchPage = useCallback(async (page) => {
        setIsLoading(true);
        try {
            const pageData = await TransferService.findAllTransfers(page, PAGE_SIZE);
            const user     = AuthService.getCurrentUser();
            let content    = pageData.content ?? [];

            if (!user?.roleNames?.includes('ADMIN')) {
                content = content.filter(t => t.createdBy === user.username);
            }

            setTransfers(content.filter(t => t.transactionStatus === 'PENDING'));
            setRejectedTransfers(content.filter(t => t.transactionStatus === 'REJECTED'));
            setConfirmedTransfers(content.filter(t => t.transactionStatus === 'SUCCESS'));
            setCurrentPage(pageData.number ?? page);
            setTotalPages(pageData.totalPages ?? 0);
            setTotalElements(pageData.totalElements ?? 0);
        } catch (error) {
            console.error('Erreur chargement transferts:', error);
            toast.error('Erreur lors du chargement des transferts');
        } finally {
            setIsLoading(false);
            setIsPageLoading(false);
        }
    }, []);

    const fetchTransfers = useCallback(() => fetchPage(currentPage), [fetchPage, currentPage]);

    useEffect(() => {
        const user = AuthService.getCurrentUser();
        if (user) setCurrentUser(user);
        fetchPage(0);
    }, [fetchPage]);

    // ─── Application du filtre ────────────────────────────────────────────────
    const handleApplyFilter = async () => {
        setFilterError('');
        setIsFilterLoading(true);

        try {
            let results = [];
            let label   = '';

            if (filterMode === 'single_date') {
                if (!filterSingleDate) { setFilterError('Veuillez sélectionner une date.'); return; }
                results = await TransferService.findTransfersByDateRange(filterSingleDate, filterSingleDate);
                label   = `Date : ${new Date(filterSingleDate).toLocaleDateString('fr-FR')}`;

            } else if (filterMode === 'date_range') {
                if (!filterStartDate && !filterEndDate) {
                    setFilterError('Veuillez sélectionner au moins une date.');
                    return;
                }
                const end = filterEndDate || formatDateParam(new Date());
                results   = await TransferService.findTransfersByDateRange(filterStartDate || null, end);
                label     = filterStartDate && filterEndDate
                    ? `Du ${new Date(filterStartDate).toLocaleDateString('fr-FR')} au ${new Date(filterEndDate).toLocaleDateString('fr-FR')}`
                    : filterStartDate
                        ? `À partir du ${new Date(filterStartDate).toLocaleDateString('fr-FR')}`
                        : `Jusqu'au ${new Date(filterEndDate).toLocaleDateString('fr-FR')}`;

            } else if (filterMode === 'by_user') {
                const targetUser = isAdmin ? selectedUser?.username : currentUser?.username;
                if (!targetUser) { setFilterError('Veuillez sélectionner un utilisateur.'); return; }
                results = await TransferService.findTransfersByCreatedBy(
                    targetUser,
                    userStartDate || null,
                    userEndDate   || null
                );
                const dateLabel = userStartDate && userEndDate
                    ? ` | Du ${new Date(userStartDate).toLocaleDateString('fr-FR')} au ${new Date(userEndDate).toLocaleDateString('fr-FR')}`
                    : userStartDate
                        ? ` | À partir du ${new Date(userStartDate).toLocaleDateString('fr-FR')}`
                        : '';
                label = `Utilisateur : ${targetUser}${dateLabel}`;
            }

            setFilterResults(results);
            setFilterLabel(label);
            setShowFilterPanel(false);

        } catch (e) {
            console.error('Erreur filtre:', e);
            setFilterError('Une erreur est survenue lors du filtrage.');
        } finally {
            setIsFilterLoading(false);
        }
    };

    const handleClearFilter = () => {
        setFilterResults(null);
        setFilterLabel('');
        setFilterError('');
        setFilterStartDate('');
        setFilterEndDate('');
        setFilterSingleDate('');
        setSelectedUser(null);
        setUserSearch('');
        setUserStartDate('');
        setUserEndDate('');
    };

    const handleResetFilterPanel = () => {
        setFilterMode('date_range');
        handleClearFilter();
        setShowFilterPanel(false);
    };

    // ─── Handlers ────────────────────────────────────────────────────────────
    const handleTransferClick     = (t)  => { setSelectedTransfer(t); setShowDetailModal(true); };
    const handleCloseDetailModal  = ()   => { setShowDetailModal(false); setSelectedTransfer(null); };
    const handleOpenPasteModal    = ()   => setShowPasteModal(true);
    const handleClosePasteModal   = ()   => setShowPasteModal(false);
    const handleOpenExportModal   = ()   => setShowExportModal(true);
    const handleCloseExportModal  = ()   => setShowExportModal(false);

    const handleTabChange = (tab) => {
        setActiveComponent(tab);
        // On ne vide pas le filtre au changement d'onglet —
        // on filtre les résultats par statut dans renderContent
    };

    const handleConfirmTransfer = async (reference) => {
        if (!reference?.trim()) { toast.error('Veuillez fournir une référence valide'); return; }
        try {
            await TransferService.confirmTransferWithRefNum({ referenceNumber: reference.trim() });
            toast.success('Transfert confirmé avec succès !');
            await fetchTransfers();
            handleClosePasteModal();
        } catch (error) {
            toast.error(error.response?.data?.message || 'Échec de la confirmation du transfert');
        }
    };

    // ─── Rendu du contenu avec filtre ─────────────────────────────────────────
    const getTabTransfers = (tab) => {
        const status = TAB_STATUS[tab];
        if (filterResults !== null) {
            return filterResults.filter(t => t.transactionStatus === status);
        }
        if (tab === 'pending')   return transfers;
        if (tab === 'confirmed') return confirmedTransfers;
        if (tab === 'rejected')  return rejectedTransfers;
        return [];
    };

    const renderContent = () => {
        if (isLoading) return <div className="text-center p-4">Chargement en cours...</div>;
        switch (activeComponent) {
            case 'pending':
                return <TransferList transfers={getTabTransfers('pending')} onTransferClick={handleTransferClick} onCopyReference={handleOpenPasteModal} onRefresh={fetchTransfers} />;
            case 'rejected':
                return <RejectedTransferList transfers={getTabTransfers('rejected')} onTransferClick={handleTransferClick} onRefresh={fetchTransfers} />;
            case 'confirmed':
                return <ConfirmedTransferList transfers={getTabTransfers('confirmed')} onTransferClick={handleTransferClick} />;
            default:
                return null;
        }
    };

    // ─── Panneau de filtre ────────────────────────────────────────────────────
    const renderFilterPanel = () => (
        <div style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 2000,
            display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
        }}
             onClick={(e) => { if (e.target === e.currentTarget) setShowFilterPanel(false); }}
        >
            <div style={{
                backgroundColor: '#fff', borderRadius: '16px 16px 0 0',
                padding: '24px', width: '100%', maxWidth: '600px',
                maxHeight: '85vh', overflowY: 'auto',
            }}>
                {/* Header panneau */}
                <div className="d-flex justify-content-between align-items-center mb-4">
                    <h5 className="mb-0 fw-bold">
                        <i className="fas fa-filter me-2" style={{ color: '#007AFF' }} />
                        Filtrer les transferts
                    </h5>
                    <button className="btn btn-sm btn-light rounded-circle" onClick={() => setShowFilterPanel(false)}>
                        <i className="fas fa-times" />
                    </button>
                </div>

                {/* Sélection du mode */}
                <p className="text-muted small fw-bold mb-2" style={{ letterSpacing: '0.5px' }}>TYPE DE FILTRE</p>
                <div className="d-flex gap-2 mb-4">
                    {[
                        { key: 'date_range',  icon: 'fas fa-calendar-alt', label: 'Intervalle'   },
                        { key: 'single_date', icon: 'fas fa-calendar-day', label: 'Date unique'  },
                        { key: 'by_user',     icon: 'fas fa-user',         label: 'Utilisateur'  },
                    ].map(m => (
                        <button
                            key={m.key}
                            className="btn btn-sm flex-fill"
                            style={{
                                borderRadius: '10px',
                                backgroundColor: filterMode === m.key ? '#007AFF' : '#f4f6fa',
                                color: filterMode === m.key ? '#fff' : '#6b7280',
                                border: filterMode === m.key ? '1.5px solid #007AFF' : '1.5px solid #e5e7eb',
                                fontWeight: filterMode === m.key ? '600' : '400',
                            }}
                            onClick={() => {
                                setFilterMode(m.key);
                                setFilterError('');
                                setSelectedUser(null);
                                setUserSearch('');
                            }}
                        >
                            <i className={`${m.icon} me-1`} /> {m.label}
                        </button>
                    ))}
                </div>

                {/* ── Mode : Date unique ── */}
                {filterMode === 'single_date' && (
                    <>
                        <p className="text-muted small fw-bold mb-2">DATE</p>
                        <input
                            type="date" className="form-control mb-3"
                            value={filterSingleDate}
                            max={formatDateParam(new Date())}
                            onChange={e => setFilterSingleDate(e.target.value)}
                            style={{ borderRadius: '10px' }}
                        />
                    </>
                )}

                {/* ── Mode : Intervalle ── */}
                {filterMode === 'date_range' && (
                    <>
                        <p className="text-muted small fw-bold mb-2">PÉRIODE</p>
                        <div className="row g-2 mb-3">
                            <div className="col-6">
                                <label className="form-label small text-muted">Date de début (optionnel)</label>
                                <input
                                    type="date" className="form-control"
                                    value={filterStartDate}
                                    max={filterEndDate || formatDateParam(new Date())}
                                    onChange={e => setFilterStartDate(e.target.value)}
                                    style={{ borderRadius: '10px' }}
                                />
                            </div>
                            <div className="col-6">
                                <label className="form-label small text-muted">Date de fin (optionnel)</label>
                                <input
                                    type="date" className="form-control"
                                    value={filterEndDate}
                                    min={filterStartDate}
                                    max={formatDateParam(new Date())}
                                    onChange={e => setFilterEndDate(e.target.value)}
                                    style={{ borderRadius: '10px' }}
                                />
                            </div>
                        </div>
                    </>
                )}

                {/* ── Mode : Par utilisateur ── */}
                {filterMode === 'by_user' && (
                    <>
                        {isAdmin ? (
                            <>
                                <p className="text-muted small fw-bold mb-2">UTILISATEUR</p>
                                <div className="position-relative mb-2">
                                    <div className="input-group" style={{ borderRadius: '10px', overflow: 'hidden' }}>
                                        <span className="input-group-text bg-light border-end-0">
                                            <i className="fas fa-search text-muted" />
                                        </span>
                                        <input
                                            type="text" className="form-control border-start-0"
                                            placeholder="Rechercher un utilisateur..."
                                            value={userSearch}
                                            onChange={e => {
                                                setUserSearch(e.target.value);
                                                if (selectedUser) setSelectedUser(null);
                                            }}
                                        />
                                        {loadingUsers && (
                                            <span className="input-group-text bg-light">
                                                <div className="spinner-border spinner-border-sm text-primary" />
                                            </span>
                                        )}
                                    </div>

                                    {/* Suggestions */}
                                    {userSuggestions.length > 0 && !selectedUser && (
                                        <div style={{
                                            position: 'absolute', top: '100%', left: 0, right: 0,
                                            backgroundColor: '#fff', border: '1px solid #e5e7eb',
                                            borderRadius: '10px', zIndex: 100,
                                            boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                                            maxHeight: '200px', overflowY: 'auto',
                                        }}>
                                            {userSuggestions.map(u => (
                                                <div
                                                    key={u.id || u.username}
                                                    className="d-flex align-items-center gap-2 p-2"
                                                    style={{ cursor: 'pointer', borderBottom: '1px solid #f3f4f6' }}
                                                    onClick={() => {
                                                        setSelectedUser(u);
                                                        setUserSearch(`${u.firstName || ''} ${u.lastName || ''} (@${u.username})`);
                                                        setUserSuggestions([]);
                                                    }}
                                                    onMouseEnter={e => e.currentTarget.style.backgroundColor = '#f0f7ff'}
                                                    onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}
                                                >
                                                    <i className="fas fa-user-circle text-primary" />
                                                    <div>
                                                        <div className="fw-semibold small">{u.firstName} {u.lastName}</div>
                                                        <div className="text-muted" style={{ fontSize: '11px' }}>@{u.username}</div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>

                                {/* Utilisateur sélectionné */}
                                {selectedUser && (
                                    <div className="d-flex align-items-center gap-2 p-2 mb-2 rounded"
                                         style={{ backgroundColor: '#e8f4ff', border: '1.5px solid #007AFF' }}>
                                        <i className="fas fa-check-circle text-primary" />
                                        <span className="text-primary fw-semibold small flex-grow-1">
                                            {selectedUser.firstName} {selectedUser.lastName} (@{selectedUser.username})
                                        </span>
                                        <button
                                            className="btn btn-sm p-0 border-0"
                                            onClick={() => { setSelectedUser(null); setUserSearch(''); }}
                                        >
                                            <i className="fas fa-times text-primary" />
                                        </button>
                                    </div>
                                )}
                            </>
                        ) : (
                            /* Non-admin : son propre compte */
                            <div className="d-flex align-items-center gap-2 p-2 mb-2 rounded"
                                 style={{ backgroundColor: '#e8f4ff', border: '1.5px solid #007AFF' }}>
                                <i className="fas fa-user text-primary" />
                                <span className="text-primary fw-semibold small">@{currentUser?.username}</span>
                            </div>
                        )}

                        {/* Dates optionnelles */}
                        <p className="text-muted small fw-bold mb-2 mt-3">PÉRIODE (optionnel)</p>
                        <div className="row g-2 mb-3">
                            <div className="col-6">
                                <label className="form-label small text-muted">Date de début</label>
                                <input
                                    type="date" className="form-control"
                                    value={userStartDate}
                                    max={userEndDate || formatDateParam(new Date())}
                                    onChange={e => setUserStartDate(e.target.value)}
                                    style={{ borderRadius: '10px' }}
                                />
                            </div>
                            <div className="col-6">
                                <label className="form-label small text-muted">Date de fin</label>
                                <input
                                    type="date" className="form-control"
                                    value={userEndDate}
                                    min={userStartDate}
                                    max={formatDateParam(new Date())}
                                    onChange={e => setUserEndDate(e.target.value)}
                                    style={{ borderRadius: '10px' }}
                                />
                            </div>
                        </div>
                    </>
                )}

                {/* Erreur */}
                {filterError && (
                    <div className="alert alert-danger py-2 d-flex align-items-center gap-2 mb-3" style={{ borderRadius: '10px' }}>
                        <i className="fas fa-exclamation-circle" />
                        <span className="small">{filterError}</span>
                    </div>
                )}

                {/* Boutons action */}
                <div className="d-flex gap-2 mt-2">
                    <button
                        className="btn btn-outline-secondary flex-fill"
                        style={{ borderRadius: '10px' }}
                        onClick={handleResetFilterPanel}
                    >
                        <i className="fas fa-times me-1" /> Réinitialiser
                    </button>
                    <button
                        className="btn btn-primary flex-fill"
                        style={{ borderRadius: '10px', backgroundColor: '#007AFF', border: 'none' }}
                        onClick={handleApplyFilter}
                        disabled={isFilterLoading}
                    >
                        {isFilterLoading
                            ? <><span className="spinner-border spinner-border-sm me-2" /> Filtrage...</>
                            : <><i className="fas fa-filter me-1" /> Appliquer</>
                        }
                    </button>
                </div>
            </div>
        </div>
    );

    // ─── Pagination ───────────────────────────────────────────────────────────
    const renderPagination = () => {
        // Pas de pagination si filtre actif
        if (filterResults !== null) return null;
        if (totalPages <= 1) return null;

        const delta = 2;
        const left  = Math.max(0, currentPage - delta);
        const right = Math.min(totalPages - 1, currentPage + delta);
        const pages = [];
        for (let i = left; i <= right; i++) pages.push(i);

        return (
            <div className="d-flex justify-content-between align-items-center mt-3 px-2 flex-wrap gap-2">
                <small className="text-muted">
                    Page {currentPage + 1} / {totalPages} &mdash; {totalElements} transfert{totalElements > 1 ? 's' : ''}
                </small>
                <div className="d-flex align-items-center gap-1 flex-wrap">
                    <Button size="sm" variant="outline-secondary" onClick={() => fetchPage(0)} disabled={currentPage === 0 || isLoading} title="Première page">«</Button>
                    <Button size="sm" variant="outline-secondary" onClick={() => fetchPage(currentPage - 1)} disabled={currentPage === 0 || isLoading}>‹</Button>
                    {left > 0 && <span className="px-1 text-muted">…</span>}
                    {pages.map(p => (
                        <Button key={p} size="sm" variant={p === currentPage ? 'primary' : 'outline-primary'} onClick={() => fetchPage(p)} disabled={isLoading}>
                            {p + 1}
                        </Button>
                    ))}
                    {right < totalPages - 1 && <span className="px-1 text-muted">…</span>}
                    <Button size="sm" variant="outline-secondary" onClick={() => fetchPage(currentPage + 1)} disabled={currentPage >= totalPages - 1 || isLoading}>›</Button>
                    <Button size="sm" variant="outline-secondary" onClick={() => fetchPage(totalPages - 1)} disabled={currentPage >= totalPages - 1 || isLoading} title="Dernière page">»</Button>
                </div>
            </div>
        );
    };

    if (isPageLoading) {
        return <LoadingSpinner isVisible={true} loadingText="Chargement des transferts..." />;
    }

    return (
        <>
            <div className="d-flex flex-column vh-100">
                <div className="flex-grow-1 overflow-y-scroll">
                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', maxWidth: '800px', margin: '0 auto' }}>
                        <div className="verify-number-main">
                            <TopTabbar />
                            <Header />
                            <div className="verify-number-top">
                                <div className="container">
                                    <div className="verify-number-top-content">
                                        <div className="setting-header" />
                                    </div>
                                    <div className="home-top-content">
                                        <div className="person-details">
                                            <p className="name">Bonjour {currentUser?.firstName}, 😉</p>
                                            <p className="balance mt-8">Content de te revoir</p>
                                        </div>
                                        <div className="balance-data text-center d-flex flex-wrap gap-2 justify-content-center">
                                            <Link to="/init_transfer">
                                                <Button variant="success" className="primary btn-lg" disabled={isLoading}>
                                                    <i className="fa-solid fa-plus me-2" />
                                                    Commencer un Transfert
                                                </Button>
                                            </Link>
                                            {isAdmin && (
                                                <Button
                                                    variant="info" className="btn-lg"
                                                    onClick={handleOpenExportModal}
                                                    disabled={isLoading}
                                                >
                                                    <i className="fas fa-download me-2" />
                                                    Exporter
                                                </Button>
                                            )}
                                            {/* Bouton Filtrer */}
                                            <Button
                                                variant={filterResults !== null ? 'warning' : 'outline-secondary'}
                                                className="btn-lg"
                                                onClick={() => setShowFilterPanel(true)}
                                                disabled={isLoading}
                                            >
                                                <i className="fas fa-filter me-2" />
                                                {filterResults !== null ? 'Filtre actif' : 'Filtrer'}
                                            </Button>
                                        </div>
                                    </div>

                                    {/* Bandeau filtre actif */}
                                    {filterResults !== null && (
                                        <div className="d-flex align-items-center gap-2 mt-2 px-3 py-2 rounded"
                                             style={{ backgroundColor: '#e8f4ff', border: '1px solid #007AFF' }}>
                                            <i className="fas fa-filter text-primary" style={{ fontSize: '13px' }} />
                                            <span className="text-primary small flex-grow-1 fw-semibold" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                {filterLabel}
                                            </span>
                                            <span className="text-muted small me-2">
                                                {filterResults.filter(t => t.transactionStatus === 'PENDING').length} en attente ·{' '}
                                                {filterResults.filter(t => t.transactionStatus === 'SUCCESS').length} complétés ·{' '}
                                                {filterResults.filter(t => t.transactionStatus === 'REJECTED').length} rejetés
                                            </span>
                                            <button
                                                className="btn btn-sm p-0 border-0"
                                                onClick={handleClearFilter}
                                                title="Supprimer le filtre"
                                            >
                                                <i className="fas fa-times text-primary" />
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="verify-number-bottom" id="homepage">
                                <div className="verify-number-bottom-wrap">
                                    <div className="homepage-bottom-content">
                                        {/* Onglets */}
                                        <div className="homepage-first" style={{ background: '#0695b3', overflowX: 'auto', whiteSpace: 'nowrap', display: 'flex', justifyContent: 'space-around', position: 'sticky', top: '50px', zIndex: 1000, padding: '8px 0' }}>
                                            {[
                                                { icon: 'fas fa-clock',        label: 'En attente', key: 'pending'   },
                                                { icon: 'fas fa-check-circle', label: 'Completes',  key: 'confirmed' },
                                                { icon: 'fa-solid fa-ban',     label: 'Rejetes',    key: 'rejected'  },
                                            ].map((item, index) => (
                                                <Button
                                                    key={index}
                                                    variant="link"
                                                    className={`first-details ${activeComponent === item.key ? 'active' : ''}`}
                                                    onClick={() => handleTabChange(item.key)}
                                                    disabled={isLoading}
                                                    style={{ minWidth: 'auto', padding: '8px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}
                                                >
                                                    <i className={item.icon} style={{ fontWeight: 'bold', color: '#f7f6f0', fontSize: '32px' }} />
                                                    <p style={{ margin: '4px 0 0 0', fontSize: '12px' }}>{item.label}</p>
                                                </Button>
                                            ))}
                                        </div>

                                        {/* Contenu + pagination */}
                                        <div className="home-activity" style={{ paddingTop: '5px' }}>
                                            <div className="home-activity-top mb-2 mt-5">
                                                <span>
                                                    {activeComponent === 'pending'   ? 'En attente'  :
                                                        activeComponent === 'confirmed' ? 'Completes'   :
                                                            activeComponent === 'rejected'  ? 'Rejetes'     : ''}
                                                </span>
                                                {/* Compteur résultats filtrés */}
                                                {filterResults !== null && (
                                                    <span className="badge bg-primary ms-2">
                                                        {getTabTransfers(activeComponent).length} résultat{getTabTransfers(activeComponent).length > 1 ? 's' : ''}
                                                    </span>
                                                )}
                                            </div>

                                            {renderContent()}
                                            {renderPagination()}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <TransferDetailModal show={showDetailModal} onHide={handleCloseDetailModal} transfer={selectedTransfer} />
                            <PasteReferenceModal show={showPasteModal} onHide={handleClosePasteModal} onPaste={handleConfirmTransfer} onRefresh={fetchTransfers} />
                            <ExportTransferModal show={showExportModal} onHide={handleCloseExportModal} transfers={{ pending: transfers, confirmed: confirmedTransfers, rejected: rejectedTransfers }} />
                            <Footer />
                        </div>
                    </div>
                </div>
            </div>

            {/* Panneau de filtre (overlay) */}
            {showFilterPanel && renderFilterPanel()}
        </>
    );
};

export default TransferPage;